'use client'

import { useState, useEffect, useRef } from 'react'
import { GiBatMask } from 'react-icons/gi'
import { BsSun, BsMoon } from 'react-icons/bs'
import { m, AnimatePresence } from 'framer-motion'
import { usePathname, useRouter } from 'next/navigation'
import { useThemeContext } from './ThemeProvider'

const topLinks = [
  { label: 'The Signal', href: '#signal', isRoute: false },
  { label: 'The Detective', href: '/about', isRoute: true },
]

const otherDropdown = [
  {
    label: 'Case Files',
    href: '#case-files',
    icon: '📁',
    desc: 'Projects & investigations',
  },
  {
    label: 'Contact',
    href: '#contact',
    icon: '📡',
    desc: 'Send a transmission',
  },
]

export default function Navbar() {
  const { isDark, toggle } = useThemeContext()
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [otherOpen, setOtherOpen] = useState(false)
  const [activeSection, setActiveSection] = useState<string>('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const pathname = usePathname()
  const router = useRouter()
  const isDetailPage = pathname?.startsWith('/projects/') ?? false

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', handler)
    return () => window.removeEventListener('scroll', handler)
  }, [])

  useEffect(() => {
    setActiveSection('')

    const sections = ['signal', 'case-files', 'contact']
    const observers: IntersectionObserver[] = []

    sections.forEach(id => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection('#' + id) },
        { threshold: 0.3 }
      )
      obs.observe(el)
      observers.push(obs)
    })

    return () => observers.forEach(o => o.disconnect())
  }, [pathname])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOtherOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const scrollTo = (href: string, isRoute: boolean) => {
    setMenuOpen(false)
    setOtherOpen(false)
    if (isRoute) {
      router.push(href)
    } else if (isDetailPage || pathname === '/about') {
      router.push('/' + href)
    } else {
      setTimeout(() => {
        document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' })
      }, 320)
    }
  }

  return (
    <m.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled || isDetailPage ? 'dark:bg-batman-black/90 bg-bruce-bg/90 backdrop-blur-md border-b dark:border-white/5 border-bruce-navy/10 shadow-lg' : 'bg-transparent'}`}
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <button onClick={() => scrollTo('#signal', false)} className="flex items-center gap-2 group">
          <m.div
            className="dark:text-batman-gold text-bruce-navy"
            whileHover={{ scale: 1.15, rotate: [0, -5, 5, 0] }}
            transition={{ duration: 0.4 }}
          >
            <GiBatMask size={32} />
          </m.div>
          <span className="font-cinzel font-semibold text-sm dark:text-batman-text text-bruce-navy tracking-widest uppercase hidden sm:block">
            Blerion Muco
          </span>
        </button>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8">
          {topLinks.map(link => {
            const isActive = link.href === '#signal'
              ? pathname === '/'
              : pathname === link.href || (isDetailPage && link.href === '/about')
            return (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href, link.isRoute)}
                className={`font-cinzel text-xs tracking-widest uppercase transition-colors duration-300 relative group ${isActive ? 'dark:text-batman-gold text-bruce-gold' : 'dark:text-batman-muted text-bruce-muted hover:dark:text-batman-gold hover:text-bruce-gold'}`}
              >
                {link.label}
                <span className={`absolute -bottom-1 left-0 right-0 h-px bg-batman-gold transition-transform duration-300 origin-left ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} />
              </button>
            )
          })}

          {/* Other dropdown */}
          {(() => {
            return (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setOtherOpen(o => !o)}
                  className="flex items-center gap-1.5 font-cinzel text-xs tracking-widest uppercase transition-colors duration-300 relative group dark:text-batman-muted text-bruce-muted hover:dark:text-batman-gold hover:text-bruce-gold"
                >
                  Other
                  <m.svg
                    viewBox="0 0 10 6"
                    className="w-2.5 h-auto fill-current"
                    animate={{ rotate: otherOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <path d="M0 0L5 6L10 0H0Z" />
                  </m.svg>
                  <span className="absolute -bottom-1 left-0 right-0 h-px bg-batman-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                </button>

                <AnimatePresence>
                  {otherOpen && (
                    <m.div
                      className="absolute right-0 top-[calc(100%+16px)] w-64 dark:bg-batman-black/95 bg-white/95 backdrop-blur-xl border dark:border-batman-gold/20 border-bruce-gold/20 rounded-xl shadow-2xl overflow-hidden"
                      initial={{ opacity: 0, y: -8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.96 }}
                      transition={{ duration: 0.18, ease: 'easeOut' }}
                    >
                      <div className="h-px bg-gradient-to-r from-transparent via-batman-gold to-transparent" />

                      <div className="p-2">
                        {otherDropdown.map((item, i) => (
                          <m.button
                            key={item.href}
                            onClick={() => scrollTo(item.href, false)}
                            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg group transition-colors duration-200 text-left ${activeSection === item.href ? 'dark:bg-batman-gold/10 bg-bruce-gold/10' : 'hover:dark:bg-batman-gold/10 hover:bg-bruce-gold/10'}`}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.06 }}
                          >
                            <span className="text-lg w-8 h-8 flex items-center justify-center rounded-lg dark:bg-batman-surface bg-gray-100 group-hover:dark:bg-batman-gold/20 group-hover:bg-bruce-gold/10 transition-colors">
                              {item.icon}
                            </span>
                            <div>
                              <p className={`font-cinzel text-xs tracking-widest uppercase transition-colors ${activeSection === item.href ? 'dark:text-batman-gold text-bruce-gold' : 'dark:text-batman-text text-bruce-navy group-hover:dark:text-batman-gold group-hover:text-bruce-gold'}`}>
                                {item.label}
                              </p>
                              <p className="font-mono text-[10px] dark:text-batman-muted text-bruce-muted mt-0.5">
                                {item.desc}
                              </p>
                            </div>
                            <span className="ml-auto dark:text-batman-gold/40 text-bruce-gold/40 group-hover:dark:text-batman-gold group-hover:text-bruce-gold transition-colors text-xs">
                              →
                            </span>
                          </m.button>
                        ))}
                      </div>

                      <div className="flex justify-center pb-3 pt-1">
                        <GiBatMask size={24} className="dark:text-batman-gold/20 text-bruce-gold/20" />
                      </div>
                    </m.div>
                  )}
                </AnimatePresence>
              </div>
            )
          })()}

          {/* Theme toggle */}
          <m.button
            onClick={toggle}
            className="w-9 h-9 rounded-full border dark:border-batman-gold/30 border-bruce-gold/40 flex items-center justify-center dark:text-batman-gold text-bruce-gold transition-transform"
            whileHover={{ scale: 1.15, rotate: 15, boxShadow: '0 0 20px rgba(200, 169, 81, 0.4)' }}
            whileTap={{ rotate: 180, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
            title={isDark ? 'Switch to Bruce Wayne mode' : 'Switch to Batman mode'}
          >
            {isDark ? <BsSun size={16} /> : <BsMoon size={16} />}
          </m.button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden dark:text-batman-text text-bruce-navy p-2"
          onClick={() => setMenuOpen(m => !m)}
          aria-label="Toggle menu"
        >
          <div className="space-y-1.5">
            <m.span className="block w-6 h-px dark:bg-batman-text bg-bruce-navy" animate={menuOpen ? { rotate: 45, y: 5 } : { rotate: 0, y: 0 }} />
            <m.span className="block w-6 h-px dark:bg-batman-text bg-bruce-navy" animate={menuOpen ? { opacity: 0 } : { opacity: 1 }} />
            <m.span className="block w-6 h-px dark:bg-batman-text bg-bruce-navy" animate={menuOpen ? { rotate: -45, y: -5 } : { rotate: 0, y: 0 }} />
          </div>
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <m.div
            className="md:hidden dark:bg-batman-black/95 bg-bruce-bg/95 backdrop-blur-md border-t dark:border-white/5 border-bruce-navy/10 px-6 pb-6"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {topLinks.map((link, i) => (
              <m.button
                key={link.href}
                onClick={() => scrollTo(link.href, link.isRoute)}
                className="block w-full text-left py-3 font-cinzel text-sm tracking-widest uppercase dark:text-batman-muted text-bruce-muted hover:text-batman-gold dark:hover:text-batman-gold transition-colors border-b dark:border-white/5 border-bruce-navy/10"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: i * 0.07 }}
              >
                {link.label}
              </m.button>
            ))}

            <m.div
              className="pt-3"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: topLinks.length * 0.07 }}
            >
              <p className="font-mono text-[10px] tracking-widest uppercase dark:text-batman-gold text-bruce-gold mb-2">
                // Other
              </p>
              {otherDropdown.map((item, i) => (
                <m.button
                  key={item.href}
                  onClick={() => scrollTo(item.href, false)}
                  className="flex items-center gap-3 w-full py-2.5 border-b dark:border-white/5 border-bruce-navy/10"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: (topLinks.length + i + 1) * 0.07 }}
                >
                  <span className="text-base">{item.icon}</span>
                  <div className="text-left">
                    <p className="font-cinzel text-sm tracking-widest uppercase dark:text-batman-muted text-bruce-muted">
                      {item.label}
                    </p>
                    <p className="font-mono text-[10px] dark:text-batman-muted/60 text-bruce-muted/60">
                      {item.desc}
                    </p>
                  </div>
                </m.button>
              ))}
            </m.div>

            <button
              onClick={toggle}
              className="mt-4 font-mono text-xs dark:text-batman-gold text-bruce-gold"
            >
              <span className="flex items-center gap-2">
                {isDark ? <BsSun size={14} /> : <BsMoon size={14} />}
                {isDark ? 'Light mode' : 'Dark mode'}
              </span>
            </button>
          </m.div>
        )}
      </AnimatePresence>
    </m.nav>
  )
}
